import { Component } from '@theme/component';

/**
 * Drives a single `--cc-scroll-progress` custom property (0 → 1) set on
 * the document root as the hero scrolls past. Consumed by CSS for a
 * small, subtle upward drift on the hero copy only — Cubby and every
 * curl are completely static and don't read this property at all. No
 * scroll hijacking — this only adds a transform on top of normal
 * scrolling.
 *
 * Progress reaches 0 while the hero still fills the viewport and 1 once
 * its bottom edge has scrolled up to the top of the viewport. Gated by
 * IntersectionObserver so the scroll listener only exists while relevant,
 * and skipped entirely under prefers-reduced-motion.
 *
 * @extends {Component}
 */
export class CurlyCubsHero extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  #ticking = false;

  connectedCallback() {
    super.connectedCallback();

    this.#ctas = this.querySelectorAll('.cc-hero__cta');
    this.#ctas.forEach((cta) => cta.addEventListener('click', this.#handleCtaClick));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.#queueUpdate, { passive: true });
          this.#queueUpdate();
        } else {
          window.removeEventListener('scroll', this.#queueUpdate);
        }
      },
      // Stays active until the hero has scrolled roughly one extra
      // viewport past the bottom of the screen, comfortably covering the
      // whole 0 -> 1 transition range computed below.
      { rootMargin: '0px 0px 1000px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('scroll', this.#queueUpdate);
    this.#observer?.disconnect();
    this.#ctas?.forEach((cta) => cta.removeEventListener('click', this.#handleCtaClick));
  }

  /** @type {NodeListOf<HTMLAnchorElement> | undefined} */
  #ctas;

  /**
   * Only intercepts same-page anchor links (href starting with #) for a
   * smooth scroll. The primary CTA's default (the Shop page URL) isn't
   * a hash, so this is currently a no-op for it — kept generic in case
   * a future .cc-hero__cta ever uses a same-page hash link again.
   */
  #handleCtaClick = (event) => {
    const href = event.currentTarget?.getAttribute('href') || '';
    if (!href.startsWith('#')) return;

    const target = document.querySelector(href);
    if (!target) return;

    event.preventDefault();
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    target.scrollIntoView({ behavior, block: 'start' });
  };

  #queueUpdate = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#ticking = false;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    let progress = 1 - rect.bottom / vh;
    progress = Math.min(1, Math.max(0, progress));
    document.documentElement.style.setProperty('--cc-scroll-progress', progress.toFixed(3));
  };
}

if (!customElements.get('curlycubs-hero')) {
  customElements.define('curlycubs-hero', CurlyCubsHero);
}
